// Xolile's Driver Pipeline comment feature. Migration 0391 (renamed from 0344).
//
// Visible to anyone with canView, the same gate the applicant detail screen
// itself uses: the whole point of routing a task back to whoever is
// assigned is that they can follow its link here and see the comment that
// raised it, and they hold canView without necessarily holding
// canCommentOnPipeline. Only the input box that POSTS a new comment is
// gated on the narrower canCommentOnPipeline as well, matching
// commentOnApplication.ts's own gate (canView && canCommentOnPipeline).
import { useEffect, useState } from 'react'
import { MessageSquare } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { useCapabilities } from '../../hooks/useCapabilities'
import { useCommentOnApplication, useGetApplicationComments } from '../../hooks/backend/interviews'
import { formatDate } from '../../utils/interviews'

type CommentRow = {
  id: string
  author_name: string | null
  author_email: string
  body: string
  routed_to_name: string | null
  routing_source: string
  created_at: string
}

function routingLabel(row: CommentRow): string {
  const who = row.routed_to_name && row.routed_to_name.trim() !== '' ? row.routed_to_name : row.author_email
  return row.routing_source === 'interview_manager'
    ? `Task routed to ${who}, the assigned interview manager`
    : `Task routed to ${who}, the pipeline fallback (nobody was assigned)`
}

export function PipelineCommentsPanel({ applicationId }: { applicationId: string }) {
  const { canView, canCommentOnPipeline } = useCapabilities()
  const comments = useGetApplicationComments<CommentRow[]>()
  const load = comments.trigger
  const post = useCommentOnApplication<{ comment_id: string }>()
  const [draft, setDraft] = useState('')

  useEffect(() => {
    if (canView) void load({ application_id: applicationId }, { skipCache: true }).catch(() => undefined)
  }, [canView, applicationId, load])

  if (!canView) return null

  const rows = comments.data ?? []

  const submit = async () => {
    const body = draft.trim()
    if (!body) return
    await post.trigger({ application_id: applicationId, body })
    setDraft('')
    void load({ application_id: applicationId }, { skipCache: true }).catch(() => undefined)
  }

  return (
    <section>
      <div className="mb-3 flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-[hsl(var(--brand))]" />
        <h3 className="text-sm font-semibold text-foreground">Comments</h3>
        {comments.loading && <Spinner className="h-3.5 w-3.5" />}
      </div>

      {comments.error && <p className="mb-2 text-xs text-rose-700 dark:text-rose-300">{comments.error}</p>}

      {rows.length === 0 ? (
        <p className="mb-3 text-sm text-muted-foreground">No comments yet.</p>
      ) : (
        <ul className="mb-3 space-y-3">
          {rows.map((row) => (
            <li key={row.id} className="rounded-md border border-border bg-card p-3">
              <div className="mb-1 flex flex-wrap items-baseline gap-2">
                <span className="text-sm font-medium text-foreground">
                  {row.author_name && row.author_name.trim() !== '' ? row.author_name : row.author_email}
                </span>
                <span className="text-xs text-muted-foreground">{formatDate(row.created_at)}</span>
              </div>
              <p className="whitespace-pre-wrap text-sm text-foreground">{row.body}</p>
              <p className="mt-1.5 text-[11px] text-muted-foreground">{routingLabel(row)}</p>
            </li>
          ))}
        </ul>
      )}

      {canCommentOnPipeline && (
        <div className="space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Leave a comment for whoever is assigned to this applicant..."
            rows={3}
            className="w-full resize-none rounded-md border border-border bg-background p-2 text-sm text-foreground"
          />
          {post.error && <p className="text-xs text-rose-700 dark:text-rose-300">{post.error}</p>}
          <Button size="sm" disabled={post.loading || draft.trim() === ''} onClick={() => void submit()}>
            {post.loading ? <Spinner className="mr-1.5 h-3.5 w-3.5" /> : null}
            Post comment
          </Button>
        </div>
      )}
    </section>
  )
}
